"use client";
import React, { useState, useEffect, useRef } from "react";
import { FileText, Pencil, Check, X, Paperclip, Trash2 } from "lucide-react";
import { REST_API_URL } from "../../api/config";
import { smartAxios } from "../../api/axios/Axios";
import toast from "react-hot-toast";

const BASE_URL = REST_API_URL;
const MAX_CHARS = 1000;

export default function YourBusinessDescription() {
  const [description, setDescription] = useState("");
  const [savedDescription, setSavedDescription] = useState("");
  const [fullName, setFullName] = useState("");           // required by PUT
  const [serverFile, setServerFile] = useState(null);   // URL string saved on server
  const [pendingFile, setPendingFile] = useState(null);   // File object selected locally (not yet saved)
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [visible, setVisible] = useState(true);           // hidden for workers
  const fileRef = useRef(null);

  // ── Fetch current description ────────────────────────────────────────────────

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await smartAxios.get("/auth/userinfo/");
        const desc = res.data?.business_description || "";
        const file = res.data?.business_description_file || null;
        const status = res.data?.user?.status || null;
        const name =
          res.data?.full_name ||
          `${res.data?.user?.first_name || ""} ${res.data?.user?.last_name || ""}`.trim();
        if (status === "worker") {
          setVisible(false);
          return;
        }
        setFullName(name);
        setDescription(desc);
        setSavedDescription(desc);
        setServerFile(file);
      } catch {
        // silently fail — component just shows empty state
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const cancelEdit = () => {
    setDescription(savedDescription);
    setPendingFile(null);
    setEditing(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
  };

  // If file is only selected locally (not yet saved) — just clear state.
  // If file exists on server — call DELETE /auth/userinfo/ to remove it.
  const handleRemoveFile = async () => {
    if (pendingFile) {
      // locally selected, not yet saved
      setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    // file is on server — delete via API
    try {
      setDeletingFile(true);
      await smartAxios.delete("/auth/userinfo/");
      setServerFile(null);
    } catch {
      toast.error("Failed to delete the file. Please try again.");
    } finally {
      setDeletingFile(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("full_name", fullName);
      fd.append("business_description", description);
      // Only append file if a new one was selected locally
      if (pendingFile) {
        fd.append("business_description_file", pendingFile);
      }

      const res = await smartAxios.put("/auth/userinfo/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const updated = res.data;
      setSavedDescription(updated?.business_description || description);
      setServerFile(updated?.business_description_file || null);
      setPendingFile(null);
      setEditing(false);
      toast.success("Business description updated!");
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── Derived display values ───────────────────────────────────────────────────
  // Show pending (local) file first, then server file
  const displayFile = pendingFile
    ? { name: pendingFile.name, isPending: true }
    : serverFile
    ? { name: serverFile.split("/").pop(), url: serverFile, isPending: false }
    : null;

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!visible) return null;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col gap-6 relative border border-gray-100">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full -translate-y-12 translate-x-12 opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-gray-50 to-gray-100 rounded-full translate-y-8 -translate-x-8 opacity-40 pointer-events-none" />

      {/* Header */}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#5563F6] rounded-xl shadow-sm">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-xl text-gray-900">Business Description</h2>
            <p className="text-sm text-gray-500 mt-0.5">About your business &amp; a description file</p>
          </div>
        </div>

        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 bg-[#5563F6] text-white rounded-xl px-4 py-2.5 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
          >
            <span className="font-medium">Edit</span>
            <Pencil className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" />
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#5563F6] text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {saving
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Check className="w-4 h-4" />}
              Save
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="relative z-10 space-y-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#5563F6] rounded-full animate-spin mb-3" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        ) : editing ? (
          <>
            {/* Text area */}
            <div className="relative">
              <textarea
                value={description}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_CHARS) setDescription(e.target.value);
                }}
                maxLength={MAX_CHARS}
                rows={5}
                placeholder="Write a description about your business..."
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#5563F6] focus:ring-2 focus:ring-indigo-100 resize-none transition-all"
              />
              <span className="absolute bottom-3 right-3 text-xs text-gray-400">
                {description.length} / {MAX_CHARS}
              </span>
            </div>

            {/* File upload */}
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Description File</p>
              {displayFile ? (
                <div className="flex items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                  <Paperclip className="w-4 h-4 text-[#5563F6] shrink-0" />
                  <span className="text-sm text-gray-700 truncate grow">
                    {displayFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    disabled={deletingFile}
                    className="shrink-0 p-1 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  >
                    {deletingFile
                      ? <div className="w-4 h-4 border-2 border-gray-300 border-t-red-400 rounded-full animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#5563F6] hover:bg-indigo-50/30 transition-all text-sm text-gray-500 hover:text-[#5563F6]"
                >
                  <Paperclip className="w-4 h-4 shrink-0" />
                  <span>Upload description file (PDF, DOC, CSV…)</span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.csv,.txt,.xls,.xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </>
        ) : (
          <>
            {/* View mode — description text */}
            {savedDescription ? (
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {savedDescription}
              </p>
            ) : (
              <div className="py-4 text-center">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No description yet</p>
                <p className="text-xs text-gray-400 mt-1">Click Edit to add a description about your business</p>
              </div>
            )}

            {/* View mode — attached file */}
            {serverFile && (
              <a
                href={serverFile}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-[#5563F6] hover:bg-indigo-50 hover:text-[#5563F6] transition-all"
              >
                <Paperclip className="w-4 h-4 shrink-0" />
                <span className="truncate max-w-xs">{serverFile.split("/").pop()}</span>
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}
